import { useEffect, useState } from "react";
import { api } from "../../api/apiClient";
import type { CatalogDraft } from "../../api/types";

/**
 * Lazy-loads a draft's OWN photo via the authenticated image endpoint.
 *
 * Extracted from `CatalogDraftsPage` so the review-pass table can show the
 * same thumbnail the detailed card does — the two views were previously
 * disagreeing about whether a draft had a photo at all, because only one of
 * them could see it.
 *
 * Not to be confused with `SupplierPicture`, which renders the 64 px picture
 * the SUPPLIER's PDF carried (Deco Spirit only). That one is read-only
 * evidence from the invoice; this one is the photo a person took.
 */
export function DraftThumb({
  draft,
  size = 56,
}: {
  draft: CatalogDraft;
  size?: number;
}) {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!draft.imageKey) {
      setUrl(null);
      return;
    }
    let cancelled = false;
    let objectUrl: string | null = null;
    void api
      .fetchCatalogDraftImageBlobUrl(draft.id, { size: "thumb" })
      .then((u) => {
        if (cancelled) {
          if (u) URL.revokeObjectURL(u);
          return;
        }
        objectUrl = u;
        setUrl(u);
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [draft.id, draft.imageKey]);

  const shell = {
    width: `${size}px`,
    height: `${size}px`,
    borderRadius: "var(--radius-md)",
    flexShrink: 0,
    objectFit: "cover" as const,
    background: "var(--color-surface-muted)",
  };

  if (!draft.imageKey || !url) {
    return <div aria-hidden style={shell} />;
  }
  return <img src={url} alt={draft.name} style={shell} loading="lazy" decoding="async" />;
}
